import React, { MutableRefObject } from 'react'
import './informed-delivery.job-view.scss'
import classnames from 'classnames'
import { IExpandedJobView } from '@halo/components/jobs-list/interfaces'
import { useLoadableDataRefState } from '@halo/hooks/use-loadable-data-ref-state'
import { useRefEffect } from '@halo/hooks/use-ref-effect'
import { LinkOutlined, Loading3QuartersOutlined } from '@ant-design/icons'
import { Descriptions, notification, Popover } from 'antd'
import { fetchDomainNotification } from '@halo/api/sf-notifications.api'
import JsonView from '@halo/components/json-view/json-view'
import { fmtDateString } from '@halo/helpers/formatting'
import aqe from '@pushly/aqe'
import { Well } from '@pushly/aqe/lib/components'

const getNotificationDetailsLink = (domainId: number, notifId: string | number) => {
    return `${aqe.defaults.publicPlatformDomain}/domains/${domainId}/notifications/${notifId}/summary`
}

const NotifLoader = (props: {
    notifId: number
    domainId: number
    children: (notif: MutableRefObject<{ loading: boolean; data?: any }>) => JSX.Element
}) => {
    const [notif, setNotif] = useLoadableDataRefState({ loading: true })
    useRefEffect(
        () => {
            return fetchDomainNotification(props.domainId, props.notifId, {
                query: {
                    includeSegments: true,
                },
            })
        },
        [false],
        (req) =>
            req.then((res) => {
                setNotif({ loading: false, data: res.data })
            }),
    )

    return <>{props.children(notif)}</>
}

const InformedDeliveryJobView = ({ job, index, expanded, indent }: IExpandedJobView) => {
    const domainId = job.payload?.domain_id
    const notifId = job.payload?.configuration?.notification_id

    return (
        <NotifLoader notifId={notifId} domainId={domainId}>
            {(notif) => {
                console.log('notif', notif)
                const { loading, data } = notif.current

                return (
                    <Well
                        className={classnames('informed-delivery-job-view')}
                        title="Notification Details"
                        hideFooter={true}
                    >
                        {loading ? (
                            <div className="label-message loading">
                                <Loading3QuartersOutlined spin={true} />
                            </div>
                        ) : !data ? (
                            <div className="label-message no-details">Unable to load Notification Details</div>
                        ) : (
                            <>
                                <Descriptions layout="horizontal" column={1}>
                                    <Descriptions.Item label="Notification ID">
                                        <Popover content="View Notification Details">
                                            <a
                                                href={getNotificationDetailsLink(data.domain_id, data.id)}
                                                target="_blank"
                                            >
                                                <LinkOutlined />
                                            </a>
                                        </Popover>
                                        <span>{data.id}</span>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Source">{data.source}</Descriptions.Item>
                                    <Descriptions.Item label="Status">{data.status}</Descriptions.Item>
                                    <Descriptions.Item label="Created">
                                        <span className="created_at">{fmtDateString(data.created_at, 'll LT')}</span>
                                        <span className="by"> by </span>
                                        <span className="created_by">{data.created_by_user_name}</span>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Meta">
                                        <JsonView data={data.meta} />
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Delivery Spec">
                                        <JsonView data={data.delivery_spec} />
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Audience">
                                        <JsonView
                                            data={data.audience}
                                            replacer={['segments', 'excluded_segments', 'id', 'name', 'is_default']}
                                        />
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Template">
                                        <JsonView data={data.template} />
                                    </Descriptions.Item>
                                </Descriptions>
                            </>
                        )}
                    </Well>
                )
            }}
        </NotifLoader>
    )
}

export default InformedDeliveryJobView
